import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { abstractBackgroundSoft } from './BackgroundPatterns';
import { techColors, neonGlow, alpha, terminalBorder, textGlowStrong, terminalTextStyle, terminalHeaderStyle, dangerButtonDefaultGlow, dangerButtonHoverStyles, positiveButtonDefaultGlow, positiveButtonHoverStyles } from './TechRetroEffects';
import { statusMessageAppearance } from './StatusMessageStyles';
import { formatUserMessage } from '../common';
import React from 'react';

export {
  ModalForm,
  ModalFormInput,
  ModalFormHint,
  ModalFormStatus,
  ModalFormStatusMessage,
  ModalForm as PasswordModalForm,
  ModalFormInput as PasswordModalInput,
  ModalFormHint as PasswordRequirements,
  ModalFormStatus as PasswordModalStatus,
  ModalFormStatusMessage as PasswordModalStatusMessage,
} from './ModalFormStyles';

export const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100vw;
  ${abstractBackgroundSoft}
  color: ${techColors.textPrimary};
  text-align: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
  ${terminalTextStyle}


  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

export const Title = styled.h1`
  font-size: 2.2em;
  color: ${techColors.neonCyan};
  margin: 0 0 1.2em;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: relative;
  display: inline-block;
  ${textGlowStrong(techColors.neonCyan)}
  ${terminalHeaderStyle}

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 48px;
    height: 2px;
    background: ${techColors.neonCyan};
    box-shadow: 0 0 10px ${techColors.neonCyan};
    border-radius: 1px;
  }

  @media (max-width: 768px) {
    font-size: 1.9em;
  }

  @media (max-width: 480px) {
    font-size: 1.6em;
    margin-bottom: 1em;
  }
`;

export const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

export const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const ProfileAvatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'color',
})`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: ${({ color }) => color || techColors.textSecondary};
  color: ${techColors.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5em;
  font-weight: 600;
  border: 2px solid ${techColors.borderSubtle};
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 2em;
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
    font-size: 1.5em;
  }
`;

export const ProfileAvatarImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${techColors.borderSubtle};

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
  }
`;

export const AvatarEditButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: ${techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1em;
  z-index: 1;
  transition: all 0.3s ease;
  ${positiveButtonDefaultGlow}

  &:hover {
    background: ${techColors.darkBg};
    ${positiveButtonHoverStyles}
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 1em;
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.9em;
  }
`;

export const AvatarDeleteButton = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid ${techColors.danger};
  background: ${techColors.darkBgTertiary};
  color: ${techColors.danger};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1em;
  z-index: 1;
  transition: all 0.3s ease;
  ${dangerButtonDefaultGlow}

  &:hover {
    background: ${techColors.darkBgTertiary};
    ${dangerButtonHoverStyles}
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 1em;
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.9em;
  }
`;

export const ProfileInfoCard = styled.div`
  width: 100%;
  max-width: 360px;
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 4px;
  background: ${techColors.darkBgSecondary};
  padding: 4px 16px;
  margin-bottom: 4px;
`;

export const ProfileInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${techColors.borderMuted};

  &:last-child {
    border-bottom: none;
  }
`;

export const ProfileInfoLabel = styled.span`
  font-size: 11px;
  color: ${techColors.textSecondary};
  text-transform: lowercase;
  flex-shrink: 0;
  ${terminalTextStyle}
`;

export const ProfileInfoValue = styled.span`
  font-size: 12px;
  color: ${techColors.textPrimary};
  text-align: right;
  word-break: break-word;
  ${terminalTextStyle}
`;

export const ProfileActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  max-width: 320px;
  margin-top: 20px;
`;

export const AvatarPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

export const AvatarPreviewContainer = styled.div`
  width: 150px;
  height: 150px;
  margin: 15px auto;
  border-radius: 50%;
  overflow: hidden;
  ${terminalBorder(techColors.neonCyan)}
  box-shadow: 0 0 10px ${alpha(techColors.neonCyan, '30')};

  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
  }
`;

export const AvatarUploadInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: 12px 16px;
  margin: 15px 0;
  ${terminalBorder(techColors.neonCyan)}
  border-radius: 4px;
  background-color: ${techColors.darkBg};
  color: ${techColors.textPrimary};
  font-size: 0.95em;
  cursor: pointer;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:focus {
    outline: none;
    ${neonGlow(techColors.neonCyan, 'strong')}
    border-color: ${techColors.neonCyan};
  }

  &::file-selector-button {
    padding: 8px 16px;
    margin-right: 12px;
    ${terminalBorder(techColors.neonCyan)}
    border-radius: 4px;
    background-color: ${techColors.darkBgTertiary};
    color: ${techColors.neonCyan};
    font-size: 0.9em;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    ${terminalTextStyle}

    &:hover {
      background-color: ${techColors.darkBg};
      ${neonGlow(techColors.neonCyan, 'normal')}
      color: ${techColors.neonCyan};
    }
  }

  @media (max-width: 480px) {
    font-size: 0.85em;
    padding: 10px 14px;
  }
`;

export const InfoText = styled.p`
  font-size: 1.2em;
  color: ${techColors.textSecondary};
  margin: 8px 0;
  ${terminalTextStyle}

  @media (max-width: 768px) {
    font-size: 1.1em;
  }

  @media (max-width: 480px) {
    font-size: 1em;
    margin: 6px 0;
  }
`;

export const Underline = styled.span`
  text-decoration: none;
  color: ${techColors.neonCyan};
`;

export const InfoValue = styled.span`
  color: ${techColors.neonCyan};
`;

export const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$enabled',
})`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  ${({ $enabled }) => ($enabled
    ? `
      color: ${techColors.online};
      border: 1px solid ${techColors.online};
      background: ${alpha(techColors.online, '15')};
    `
    : `
      color: ${techColors.textSecondary};
      border: 1px solid ${techColors.borderSubtle};
      background: ${techColors.darkBgTertiary};
    `)}
  ${terminalTextStyle}
`;

export const Button = styled(Link)`
  display: block;
  color: ${techColors.textPrimary};
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 16px;
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 4px;
  background-color: ${techColors.darkBgTertiary};
  margin: 0;
  text-align: center;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  ${terminalTextStyle}
  
  &:hover {
    background-color: ${techColors.darkBg};
    border-color: rgba(255, 255, 255, 0.28);
    ${neonGlow(techColors.neonCyan, 'subtle')}
  }
`;

export const ActionButton = styled.button`
  color: ${techColors.textPrimary};
  font-size: 13px;
  font-weight: 500;
  padding: 10px 16px;
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 4px;
  background-color: ${techColors.darkBgTertiary};
  margin: 0;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    background-color: ${techColors.darkBg};
    border-color: rgba(255, 255, 255, 0.28);
    ${neonGlow(techColors.neonCyan, 'subtle')}
  }
`;

export const DestructiveButton = styled.button`
  color: ${techColors.danger};
  font-size: 13px;
  font-weight: 500;
  padding: 10px 16px;
  border: 1px solid ${techColors.danger};
  border-radius: 4px;
  background-color: ${alpha(techColors.danger, '10')};
  margin: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  ${terminalTextStyle}
  ${dangerButtonDefaultGlow}

  &:hover {
    background-color: ${techColors.darkBgTertiary};
    ${dangerButtonHoverStyles}
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const ModalContainer = styled.div`
  background-color: ${techColors.darkBgSecondary};
  ${terminalBorder(techColors.neonPurple)}
  padding: 12px;
  border-radius: 4px;
  max-width: 380px;
  width: 90%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-shadow: 0 0 30px ${alpha(techColors.neonPurple, '40')}, 0 0 60px ${alpha(techColors.neonPurple, '20')};
  transition: all 0.3s ease;
  animation: slideUp 0.3s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${techColors.neonPurple};
    background-size: 200% 100%;
    animation: shimmer 3s linear infinite;
    box-shadow: 0 0 10px ${techColors.neonPurple};
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @media (max-width: 768px) {
    padding: 12px;
    max-width: 400px;
    border-radius: 8px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    max-width: 90%;
    border-radius: 8px;
    gap: 8px;
  }
`;

export const ModalTitle = styled.p`
  font-size: 14px;
  color: ${techColors.neonPurple};
  font-weight: 600;
  margin-bottom: 3px;
  ${terminalHeaderStyle}
`;

export const ModalText = styled.p`
  font-size: 12px;
  color: ${techColors.textPrimary};
  font-weight: 400;
  margin-bottom: 3px;
  ${terminalTextStyle}
`;

export const WarningText = styled.p`
  font-size: 12px;
  color: ${techColors.danger};
  font-weight: 500;
  margin: 8px 0 6px 0;
  text-align: center;
  line-height: 1.4;
  ${terminalTextStyle}

  strong {
    font-weight: 700;
    color: ${techColors.danger};
  }
`;

export const WarningList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 6px 0 0 0;
  text-align: left;
  width: 100%;
  max-width: 350px;
`;

export const WarningItem = styled.li`
  font-size: 11px;
  color: ${techColors.textPrimary};
  font-weight: 400;
  margin: 4px 0;
  padding-left: 6px;
  line-height: 1.3;
  ${terminalTextStyle}
`;

export const ModalTextContainer = styled.div`
  min-height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  position: relative;
  width: 100%;
  margin-bottom: 6px;
  padding-bottom: 0; /* Убрали лишний отступ */
`;

const StatusMessageBox = styled.p.withConfig({
  shouldForwardProp: (prop) => prop !== 'success',
})`
  position: relative;
  ${statusMessageAppearance}
  font-size: 11px;
  border-radius: 4px;
  padding: 6px 10px;
  min-height: 28px; /* Минимальная высота для одной строки */
  max-height: 50px; /* Максимальная высота для 2-3 строк */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: calc(100% - 24px);
  max-width: 300px;
  margin: 0 auto 10px auto;
  white-space: normal;
  word-wrap: break-word;
  overflow: hidden;
  box-sizing: border-box;
  visibility: ${({ children }) => (children && children.trim() ? 'visible' : 'hidden')};
  opacity: ${({ children }) => (children && children.trim() ? 1 : 0)};
  transition: opacity 0.3s ease-out, visibility 0.3s ease-out;
  /* Убрали анимацию slideDown, чтобы не было прыжка */
`;

export const StatusMessage = ({ success, children, ...props }) => (
  <StatusMessageBox success={success} {...props}>
    {typeof children === 'string' || typeof children === 'number'
      ? formatUserMessage(children)
      : children}
  </StatusMessageBox>
);

export const Input = styled.input`
  padding: 6px 10px;
  margin: 0 0 10px 0;
  ${terminalBorder(techColors.neonCyan)}
  border-radius: 4px;
  width: 100%;
  max-width: 380px; /* Расширили для паролей */
  font-size: 12px;
  text-align: center;
  background-color: ${techColors.darkBg};
  color: ${techColors.textPrimary};
  transition: all 0.3s ease;
  ${terminalTextStyle}

  &:focus {
    outline: none;
    ${neonGlow(techColors.neonCyan, 'strong')}
    border-color: ${techColors.neonCyan};
    background-color: ${techColors.darkBg};
    transform: translateY(-1px);
  }

  &::placeholder {
    color: ${techColors.textSecondary};
    opacity: 0.6;
    text-align: center;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 11px;
    padding: 6px 10px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    font-size: 11px;
    padding: 6px 10px;
    margin-bottom: 8px;
  }
`;

export const ConfirmationInput = styled(Input).withConfig({
  shouldForwardProp: (prop) => prop !== 'isPassword',
})`
  width: 100%;
  max-width: ${({ isPassword }) => (isPassword ? '250px' : '130px')}; /* Для пароля: 380px, для кода: 130px */
  padding: 6px 10px;
  margin-bottom: 10px;
  font-size: 12px;
  text-align: center;

  @media (max-width: 768px) {
    max-width: ${({ isPassword }) => (isPassword ? '100%' : '130px')};
    font-size: 11px;
    padding: 6px 10px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    max-width: ${({ isPassword }) => (isPassword ? '100%' : '130px')};
    font-size: 11px;
    padding: 6px 10px;
    margin-bottom: 8px;
  }
`;

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
`;

export const ModalButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'primary' && prop !== '$danger',
})`
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 4px;
  cursor: pointer;
  margin: 0;
  transition: all 0.3s ease;
  ${terminalTextStyle}

  ${({ $danger }) => $danger && `
    color: ${techColors.danger};
    background-color: ${techColors.darkBgSecondary};
    border: 1px solid ${techColors.danger};
    ${dangerButtonDefaultGlow}
    &:hover {
      background-color: ${techColors.darkBgTertiary};
      ${dangerButtonHoverStyles}
    }
  `}

  ${({ primary, $danger }) => primary && !$danger && `
    color: ${techColors.textPrimary};
    background-color: ${techColors.darkBgSecondary};
    border: 1px solid rgba(255, 255, 255, 0.28);
    ${positiveButtonDefaultGlow}
    &:hover {
      background-color: ${techColors.darkBgTertiary};
      ${positiveButtonHoverStyles}
    }
  `}

  ${({ primary, $danger }) => !primary && !$danger && `
    color: ${techColors.textSecondary};
    background-color: ${techColors.darkBgTertiary};
    border: 1px solid ${techColors.borderSubtle};
    &:hover {
      background-color: ${techColors.darkBg};
      color: ${techColors.textPrimary};
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 5px 12px;
    font-size: 11px;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 6px;
  margin-top: 0;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 5px;
  }
`;

export const ConnectIdContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 8px 0;
`;

export const CopyButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${techColors.borderSubtle};
  background-color: ${techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: ${techColors.darkBg};
    border-color: rgba(255, 255, 255, 0.28);
    ${neonGlow(techColors.neonCyan, 'subtle')}
  }
`;

export const CopyNotification = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'show',
})`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${techColors.darkBgSecondary};
  ${terminalBorder(techColors.neonCyan)}
  ${neonGlow(techColors.neonCyan, 'normal')}
  color: ${techColors.neonCyan};
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85em;
  white-space: nowrap;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
  z-index: 100;
  ${terminalTextStyle}

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid ${techColors.darkBgSecondary};
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const DeletingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid ${alpha(techColors.neonCyan, '30')};
  border-top: 4px solid ${techColors.neonCyan};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto;
  box-shadow: 0 0 15px ${alpha(techColors.neonCyan, '40')};
`;
